import React, { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import AdminTable from '../components/AdminTable';
import AdminModal from '../components/AdminModal';
import styles from './CarouselManagement.module.css';
import { ImageIcon, PlusIcon } from '../../components/Icons';

const initialFormState = {
  title: '',
  image_url: '',
  link_url: '',
  position: 0,
  is_active: true,
};

function CarouselManagement() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(initialFormState);

  useEffect(() => {
    fetchCarouselItems();
  }, []);

  const fetchCarouselItems = async () => {
    try {
      setLoading(true);
      const response = await api.get('/carousel-items/');
      setItems(response.data || []);
    } catch (error) {
      console.error('获取轮播图数据失败:', error);
      alert('获取轮播图数据失败，请稍后再试');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingItem(null);
    setFormData(initialFormState);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title || '',
      image_url: item.image_url || '',
      link_url: item.link_url || '',
      position: Number.isFinite(item.position) ? item.position : 0,
      is_active: Boolean(item.is_active),
    });
    setShowModal(true);
  };

  const handleDelete = async (item) => {
    if (!item?.id) return;
    if (window.confirm(`确定要删除轮播图「${item.title || item.image_url}」吗？`)) {
      try {
        await api.delete(`/carousel-items/${item.id}`);
        await fetchCarouselItems();
        alert('轮播图删除成功');
      } catch (error) {
        console.error('删除轮播图失败:', error);
        alert('删除轮播图失败，请稍后再试');
      }
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.image_url.trim()) {
      alert('请填写轮播图图片地址');
      return;
    }

    const payload = {
      title: formData.title.trim() || null,
      image_url: formData.image_url.trim(),
      link_url: formData.link_url.trim() || null,
      position: Number.isFinite(Number(formData.position))
        ? Number(formData.position)
        : 0,
      is_active: Boolean(formData.is_active),
    };

    try {
      if (editingItem) {
        await api.put(`/carousel-items/${editingItem.id}`, payload);
        alert('轮播图更新成功');
      } else {
        await api.post('/carousel-items/', payload);
        alert('轮播图创建成功');
      }
      setShowModal(false);
      await fetchCarouselItems();
    } catch (error) {
      console.error('保存轮播图失败:', error);
      alert('保存轮播图失败，请稍后再试');
    }
  };

  const activeCount = useMemo(
    () => items.filter((item) => item.is_active).length,
    [items],
  );

  const columns = [
    { key: 'position', label: '位置', width: '90px' },
    {
      key: 'title',
      label: '标题',
      render: (value) => value || '—',
    },
    {
      key: 'image_url',
      label: '图片',
      render: (value, row) =>
        value ? (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.thumbnailLink}
          >
            <img
              src={value}
              alt={row.title || '轮播图'}
              className={styles.thumbnail}
            />
          </a>
        ) : (
          '—'
        ),
    },
    {
      key: 'link_url',
      label: '跳转链接',
      render: (value) =>
        value ? (
          <a
            href={value}
            target={value.startsWith('http') ? '_blank' : '_self'}
            rel={value.startsWith('http') ? 'noopener noreferrer' : undefined}
            className={styles.link}
          >
            {value}
          </a>
        ) : (
          '—'
        ),
    },
    {
      key: 'is_active',
      label: '状态',
      render: (value) => (
        <span
          className={`${styles.statusBadge} ${
            value ? styles.statusActive : styles.statusInactive
          }`}
        >
          {value ? '启用' : '停用'}
        </span>
      ),
    },
  ];

  return (
    <div className={styles.carouselManagement}>
      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>轮播图管理</h1>
          <p className={styles.subtitle}>管理首页轮播图的展示内容</p>
        </div>
        <button type="button" className={styles.createBtn} onClick={handleCreate}>
          <PlusIcon size={18} />
          新增轮播图
        </button>
      </div>

      <div className={styles.stats}>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <ImageIcon size={24} />
          </div>
          <div>
            <div className={styles.statNumber}>{items.length}</div>
            <div className={styles.statLabel}>轮播图总数</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <ImageIcon size={24} />
          </div>
          <div>
            <div className={styles.statNumber}>{activeCount}</div>
            <div className={styles.statLabel}>正在展示</div>
          </div>
        </div>
      </div>

      <AdminTable
        data={items}
        columns={columns}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        emptyMessage="暂无轮播图数据"
      />

      {showModal && (
        <AdminModal
          title={editingItem ? '编辑轮播图' : '新增轮播图'}
          onClose={() => setShowModal(false)}
          onSubmit={handleSubmit}
          size="medium"
        >
          <div className={styles.form}>
            <div className={styles.formGroup}>
              <label className={styles.label}>标题（选填）</label>
              <input
                type="text"
                className={styles.input}
                value={formData.title}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, title: e.target.value }))
                }
                placeholder="请输入轮播图标题"
              />
              <p className={styles.inputHint}>用于展示在图片上的文字说明</p>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>图片地址</label>
              <input
                type="url"
                className={styles.input}
                value={formData.image_url}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, image_url: e.target.value }))
                }
                placeholder="请输入图片的完整 URL"
                required
              />
              <p className={styles.inputHint}>建议使用 16:9 的高质量图片链接</p>
              {formData.image_url && (
                <div className={styles.preview}>
                  <img
                    src={formData.image_url}
                    alt="轮播图预览"
                    className={styles.previewImage}
                  />
                </div>
              )}
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>跳转链接（选填）</label>
              <input
                type="url"
                className={styles.input}
                value={formData.link_url}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, link_url: e.target.value }))
                }
                placeholder="点击轮播图后跳转的地址"
              />
            </div>

            <div className={styles.inlineGroup}>
              <div className={styles.formGroup}>
                <label className={styles.label}>位置序号</label>
                <input
                  type="number"
                  className={styles.input}
                  value={formData.position}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      position: e.target.value,
                    }))
                  }
                  placeholder="数字越小越靠前"
                  step="1"
                />
              </div>

              <label className={`${styles.formGroup} ${styles.checkboxGroup}`}>
                <span className={styles.label}>启用状态</span>
                <div className={styles.checkboxWrapper}>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        is_active: e.target.checked,
                      }))
                    }
                  />
                  <span>{formData.is_active ? '启用' : '停用'}</span>
                </div>
              </label>
            </div>
          </div>
        </AdminModal>
      )}
    </div>
  );
}

export default CarouselManagement;
